import React from 'react';
import { Empresa } from '@/types/empresa';
import { useAuth } from '@/hooks/useAuth';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';

interface EmpresaTableProps {
  empresas: Empresa[];
  onView: (empresa: Empresa) => void;
}

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

export const EmpresaTable: React.FC<EmpresaTableProps> = ({ empresas, onView }) => {
  const { isFieldAllowed } = useAuth();
  const canNombre = isFieldAllowed('nombre');
  const canRegion = isFieldAllowed('region');
  const canEstatus = isFieldAllowed('estatus');
  const canVoboRequerido = isFieldAllowed('voboRequerido');
  const canNivel = isFieldAllowed('nivel');

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
      <table className="min-w-full divide-y divide-gray-200 bg-white">
        <thead className="bg-gray-50">
          <tr>
            {canNombre && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
            )}
            {canRegion && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Región</th>
            )}
            {canEstatus && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estatus</th>
            )}
            {canVoboRequerido && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requiere VoBo</th>
            )}
            {canNivel && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nivel</th>
            )}
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {empresas.map((empresa) => (
            <tr key={empresa.id} className="hover:bg-gray-50 transition-colors duration-150">
              {canNombre && (
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-bold text-gray-900">{empresa.nombre}</div>
                <div className="text-xs text-gray-500">{empresa.contacto}</div>
              </td>
              )}
              {canRegion && (
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {empresa.region || '—'}
              </td>
              )}
              {canEstatus && (
              <td className="px-6 py-4 whitespace-nowrap">
                <Badge variant={empresa.estatus === 'Activa' ? 'success' : 'default'}>
                  {empresa.estatus}
                </Badge>
              </td>
              )}
              {canVoboRequerido && (
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {empresa.voboRequerido || '—'}
              </td>
              )}
              {canNivel && (
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                {empresa.nivel || '—'}
              </td>
              )}
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onView(empresa)}
                  title="Editar Empresa"
                >
                  <EditIcon />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
